import { FormEvent, useState } from 'react';
import { Button, Heading, Input } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import { FesConfig, Team, setTeamAzureDevOps } from '../../fes';

export function hasActiveAzureDevOpsToken(team: Team | null): boolean {
  return Boolean(team?.triggerTokens?.some(token => token.channel === 'azure-devops' && token.status === 'active'));
}

export function AzureDevOpsPanel({
  fesConfig,
  onSaved,
  team,
  tokens,
}: {
  fesConfig: FesConfig;
  onSaved: (team: Team) => void;
  team: Team;
  tokens: AuthTokens;
}) {
  const [statusCallbackUrl, setStatusCallbackUrl] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  const settings = team.azureDevOps;
  const isSet = Boolean(settings?.statusCallbackUrlMasked);
  const trimmedUrl = statusCallbackUrl.trim();
  const showForm = !isSet || isEditing;

  const startEditing = () => {
    setSaved(false);
    setError(undefined);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setStatusCallbackUrl('');
    setError(undefined);
    setIsEditing(false);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedUrl) {
      return;
    }

    setIsSaving(true);
    setError(undefined);
    setSaved(false);

    try {
      const response = await setTeamAzureDevOps(fesConfig, tokens, team.teamId, { statusCallbackUrl: trimmedUrl });
      // The full URL is never read back, so the field is cleared once it is stored.
      setStatusCallbackUrl('');
      setIsEditing(false);
      setSaved(true);
      if (response.data) {
        onSaved(response.data);
      }
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save the status callback URL.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="app-team-panel" aria-labelledby="team-azure-devops-heading">
      <div className="app-team-panel-header">
        <div>
          <div className="app-kicker">Team Detail</div>
          <Heading level={2} id="team-azure-devops-heading" className="app-panel-title">
            Azure DevOps
          </Heading>
          <p className="app-panel-subtitle">Status updates of this team&apos;s Azure DevOps jobs are posted to the status callback URL.</p>
        </div>
      </div>

      {error ? (
        <div className="app-result app-team-error" role="alert">
          {error}
        </div>
      ) : null}

      <dl className="app-azure-callback-current">
        <dt>Active status callback URL</dt>
        <dd>
          {isSet ? (
            <code className="app-azure-callback-url">{settings?.statusCallbackUrlMasked}</code>
          ) : (
            'Not set'
          )}
        </dd>
        <dd className="app-field-hint" role="status">
          {isSet
            ? 'The signature is never shown.'
            : 'Until a URL is set, this team\'s Azure DevOps jobs send no status callbacks.'}
          {saved ? ' Saved.' : ''}
        </dd>
      </dl>

      {showForm ? (
        <form className="app-azure-callback-form" onSubmit={handleSubmit}>
          <label className="app-form-field" htmlFor="team-azure-callback-url">
            {isSet ? 'New status callback URL' : 'Status callback URL'}
          </label>
          <div className="app-azure-callback-row">
            <Input
              id="team-azure-callback-url"
              type="url"
              autoComplete="off"
              placeholder="https://…logic.azure.com/workflows/…"
              value={statusCallbackUrl}
              onChange={event => setStatusCallbackUrl(event.target.value)}
              disabled={isSaving}
            />
            <Button type="submit" disabled={!trimmedUrl || isSaving}>
              {isSaving ? 'Saving...' : 'Save URL'}
            </Button>
            {isSet ? (
              <Button type="button" secondary onClick={cancelEditing} disabled={isSaving}>
                Cancel
              </Button>
            ) : null}
          </div>
        </form>
      ) : (
        <div className="app-actions">
          <Button type="button" secondary onClick={startEditing}>
            Change URL
          </Button>
        </div>
      )}
    </section>
  );
}
